import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type Vehicle = {
  key: string
  id: string
  vehicleNo: string
  vehicleModel: string
  img: string
  madeofYear: string
  registrationNo: string
  chassisNo: string
  gps: string
  name: string
  phone: string
  status: "Active" | "Inactive"
  statusClass: string
}

export interface Driver {
  id: string
  key: string
  name: string
  phone: string
  driverLicenseNo: string
  cnic?: string
  address: string
  status: string
  statusClass: string
  driverImg: string
  first_name?: string
  last_name?: string
  contact_number?: string
  license_number?: string
  joining_date?: string | null
  assignments?: { vehicleId: any; shiftId: any; route: string }[]
  licenseImage?: string
  cnicImage?: string
}

export type PaginationData = {
  currentPage: number
  hasNextPage: boolean
  hasPrevPage: boolean
  limit: number
  total: number
  totalPages: number
}

export type VehiclesResponse = {
  vehicles: Vehicle[]
  pagination: PaginationData
}

export type DriversResponse = {
  data: Driver[]
  pagination: PaginationData
}

const TRANSPORT_ENDPOINT = {
  DRIVERS: "/driver",
}

const getAllDrivers = async ({
  page = 1,
  limit = 20,
  searchText,
  status,
  sortBy,
  sortOrder,
}: {
  page?: number
  limit?: number
  searchText?: string
  status?: string
  sortBy?: string
  sortOrder?: string
} = {}): Promise<DriversResponse> => {
  const response = await apiClient({
    url: appendQueryParams(TRANSPORT_ENDPOINT.DRIVERS, {
      page,
      limit,
      searchText,
      status,
      sortBy,
      sortOrder,
    }),
    method: "GET",
  })
  return response.data
}

/** Shifts associated with the selected vehicle (for driver assignment flow) */
const getShiftsByVehicle = async (
  vehicleId: string,
): Promise<{ _id: string; name?: string }[]> => {
  const response = await apiClient({
    url: `${TRANSPORT_ENDPOINT.DRIVERS}/vehicles/${vehicleId}/shifts`,
    method: "GET",
  })
  return Array.isArray(response.data) ? response.data : []
}

/** Routes (strings) for the selected vehicle + shift (for driver assignment flow) */
const getRoutesByVehicleAndShift = async (
  vehicleId: string,
  shiftId: string,
): Promise<string[]> => {
  const response = await apiClient({
    url: `${TRANSPORT_ENDPOINT.DRIVERS}/vehicles/${vehicleId}/shifts/${shiftId}/routes`,
    method: "GET",
  })
  return Array.isArray(response.data) ? response.data : []
}

export type DriverAssignment = {
  vehicleId: string
  shiftId: string
  route: string
}

const createDriver = async (driverData: {
  first_name?: string
  last_name?: string
  name?: string
  contact_number: string
  phoneNo?: string
  license_number?: string
  address: string
  status?: string
  cnic?: string
  cnic_number?: string
  joining_date?: string | null
  joiningDate?: string | null
  assignments: DriverAssignment[]
}) => {
  const payload: Record<string, unknown> = {
    first_name:
      driverData.first_name ??
      (driverData.name ? driverData.name.split(/\s+/)[0] : ""),
    last_name:
      driverData.last_name ??
      (driverData.name && driverData.name.split(/\s+/).length > 1
        ? driverData.name.split(/\s+/).slice(1).join(" ")
        : ""),
    contact_number: driverData.contact_number || driverData.phoneNo,
    address: driverData.address,
    status: driverData.status || "active",
    cnic_number: driverData.cnic_number ?? driverData.cnic,
    assignments: driverData.assignments,
  }
  if (driverData.license_number != null)
    payload.license_number = driverData.license_number
  if (driverData.joining_date != null)
    payload.joining_date = driverData.joining_date
  if (driverData.joiningDate != null)
    payload.joining_date = driverData.joiningDate
  const response = await apiClient({
    url: TRANSPORT_ENDPOINT.DRIVERS,
    method: "POST",
    data: payload,
  })
  return response.data
}

const deleteDriver = async (driverId: string) => {
  const response = await apiClient({
    url: `${TRANSPORT_ENDPOINT.DRIVERS}/${driverId}`,
    method: "DELETE",
  })
  return response.data
}

const updateDriver = async (
  driverId: string,
  driverData: {
    first_name?: string
    last_name?: string
    contact_number?: string
    license_number?: string
    address?: string
    status?: string
    joining_date?: string | null
    joiningDate?: string | null
    assignments?: DriverAssignment[]
  },
) => {
  const payload = { ...driverData }
  if (payload.joiningDate != null && payload.joining_date == null)
    payload.joining_date = payload.joiningDate
  const response = await apiClient({
    url: `${TRANSPORT_ENDPOINT.DRIVERS}/${driverId}`,
    method: "PUT",
    data: payload,
  })
  return response.data
}

/** Upload driver documents (driving license + CNIC images).
 * Backend: PATCH /driver/:id/documents, multer fields: drivingLicense, cnic */
const uploadDriverDocuments = async (
  driverId: string,
  formData: FormData,
): Promise<{ licenseImage?: string; cnicImage?: string }> => {
  const response = await apiClient({
    url: `${TRANSPORT_ENDPOINT.DRIVERS}/${driverId}/documents`,
    method: "PATCH",
    data: formData,
  })
  return response.data?.data ?? response.data ?? {}
}

export {
  getAllDrivers,
  createDriver,
  deleteDriver,
  updateDriver,
  getShiftsByVehicle,
  getRoutesByVehicleAndShift,
  uploadDriverDocuments,
}
